
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useNavigationBadges } from '@/hooks/useNavigationBadges';
import ThemeToggle from './ThemeToggle';
import { useDeviceType } from '@/hooks/useDeviceType';
import PropertyShieldLogo from '@/components/ui/PropertyShieldLogo';
import { cn } from '@/lib/utils';

interface NavbarProps {
  user: { name: string; role: string } | null;
  onLogout: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { isMobile, isTablet, isDesktop } = useDeviceType();
  const { data: badges } = useNavigationBadges();
  const activeAlertCount = badges?.alerts ?? 0;
  
  return (
    <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className={cn(
        "flex items-center gap-4",
        // Responsive height and padding
        isMobile ? "h-14 px-3" : isTablet ? "h-16 px-4" : "h-14 px-4"
      )}>
        <div className="flex items-center gap-2 flex-1">
          {/* Sidebar trigger - always visible */}
          <SidebarTrigger className={cn(
            "touch-target",
            isMobile ? "h-9 w-9" : "h-8 w-8"
          )} />

          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <PropertyShieldLogo size={isMobile ? 'sm' : isTablet ? 'md' : 'md'} />
          </div>
        </div>

        <div className={cn(
          "flex items-center",
          isMobile ? "gap-2" : isTablet ? "gap-3" : "gap-3"
        )}>
          {user && (
            <>
              <ThemeToggle />
              
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "relative touch-target",
                  isMobile ? "h-9 w-9" : "h-8 w-8"
                )}
                onClick={() => navigate('/alerts')}
              >
                <Bell className={cn(
                  isMobile ? "h-5 w-5" : "h-4 w-4"
                )} />
                {activeAlertCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-4 h-4 flex items-center justify-center text-xs font-medium">
                    {activeAlertCount}
                  </span>
                )}
              </Button>

              <div className="flex items-center gap-2">
                {/* Show user info on tablet and desktop */}
                {(isTablet || isDesktop) && (
                  <div className="text-right">
                    <div className={cn(
                      "font-medium",
                      isTablet ? "text-sm" : "text-sm"
                    )}>
                      {user.name}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {user.role}
                    </div>
                  </div>
                )}
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "sm"}
                  className="touch-target-sm"
                  onClick={onLogout}
                >
                  {isMobile ? 'Out' : 'Logout'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
